import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  ExternalLink,
  KeyRound,
  Lock,
  Pencil,
  Plus,
  Share2,
  ShieldCheck,
} from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { CopyButton, KV, PersonAvatar } from '@/components/catalog/CatalogParts';
import {
  AccessBadge,
  LoginRoleBadge,
  OnlineAccountStatusBadge,
} from '@/components/catalog/OnlineAccountBadges';
import { OnlineAccountDialog } from '@/components/catalog/OnlineAccountDialog';
import { OnlineAccountShareDialog } from '@/components/catalog/OnlineAccountShareDialog';
import { OnlineLoginDialog } from '@/components/catalog/OnlineLoginDialog';
import { SavedPassword } from '@/components/catalog/PasswordParts';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { Skeleton } from '@/components/ui/Skeleton';
import { useOnlineAccountsCatalog } from '@/features/onlineAccounts/hooks';
import {
  ONLINE_ACCOUNT_CATEGORY_LABELS,
  SHARE_LEVEL_DESCRIPTIONS,
  canEditAccount,
  canShareAccount,
  describeAccess,
  hasLiveAccess,
  isShareActive,
  loginAccessFor,
  loginHolderName,
  profileName,
  type OnlineAccountRow,
  type OnlineLogin,
  type OnlineShare,
} from '@/features/onlineAccounts/model';
import { useSoftwareSubscriptions } from '@/features/software/hooks';
import { durationSince, websiteHref, websiteLabel, type SeatProfile } from '@/features/software/model';
import { formatDate, formatDateOnly, formatPhone } from '@/lib/formatters';
import type { OnlineLoginRole } from '@/types/database';

/** One online account: where it lives, what's on file, its logins, and who can see it. */
export function OnlineAccountDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { rows, myShares, people, shareable, superAdmins, profilesById, isLoading, error } =
    useOnlineAccountsCatalog();
  const [editing, setEditing] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [loginDialog, setLoginDialog] = useState<{
    login: OnlineLogin | null;
    role: OnlineLoginRole;
  } | null>(null);

  const row = rows.find((r) => r.id === id) ?? null;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <BackLink />
        <Skeleton className="h-10 w-64" />
        <div className="grid gap-6 lg:grid-cols-3">
          <Skeleton className="h-72 lg:col-span-2" />
          <Skeleton className="h-72" />
        </div>
      </div>
    );
  }

  if (error || !row) {
    return (
      <div className="space-y-6">
        <BackLink />
        <EmptyState
          icon={error ? KeyRound : Lock}
          title={error ? 'Could not load this account' : 'Account not found'}
          detail={
            error instanceof Error
              ? error.message
              : 'It may have been deleted, or it hasn’t been shared with you.'
          }
          action={
            <Button asChild variant="outline">
              <Link to="/catalog/software/accounts">Back to accounts</Link>
            </Button>
          }
        />
      </div>
    );
  }

  const canEdit = canEditAccount(row.myAccess);
  const canShare = canShareAccount(row.myAccess);
  const activeLogin = loginDialog?.login ?? null;
  const activeLoginAccess = activeLogin
    ? loginAccessFor(activeLogin, row.myAccess, myShares)
    : row.myAccess;

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <BackLink />
        <PageHeader
          className="pb-0"
          eyebrow="Catalog · Software · Accounts"
          title={row.name}
          meta={
            <span className="flex flex-wrap items-center gap-2">
              <Badge variant="secondary">{ONLINE_ACCOUNT_CATEGORY_LABELS[row.category]}</Badge>
              <OnlineAccountStatusBadge status={row.status} />
              {row.myAccess && row.myAccess !== 'owner' && (
                <span className="inline-flex items-center gap-1.5">
                  <span className="text-muted-foreground">Your access:</span>
                  <AccessBadge access={row.myAccess} />
                </span>
              )}
            </span>
          }
          actions={
            canEdit || canShare ? (
              <div className="flex flex-wrap gap-2">
                {canShare && (
                  <Button variant="outline" onClick={() => setSharing(true)}>
                    <Share2 className="h-4 w-4" />
                    Share
                  </Button>
                )}
                {canEdit && (
                  <Button variant="outline" onClick={() => setEditing(true)}>
                    <Pencil className="h-4 w-4" />
                    Edit
                  </Button>
                )}
              </div>
            ) : undefined
          }
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <LoginsCard
            row={row}
            myShares={myShares}
            profilesById={profilesById}
            onAdd={(role) => setLoginDialog({ login: null, role })}
            onOpen={(login) => setLoginDialog({ login, role: login.role })}
          />
          {row.notes && (
            <Card>
              <CardHeader>
                <CardTitle>Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="whitespace-pre-wrap text-sm leading-body text-foreground">
                  {row.notes}
                </p>
              </CardContent>
            </Card>
          )}
        </div>

        <div className="space-y-6">
          <DetailsCard row={row} />
          <CreatedCard row={row} profilesById={profilesById} />
          <AccessCard
            row={row}
            myShares={myShares}
            superAdmins={superAdmins}
            profilesById={profilesById}
            onShare={() => setSharing(true)}
          />
        </div>
      </div>

      {canEdit && (
        <OnlineAccountDialog
          open={editing}
          account={row}
          people={people}
          canDelete={row.myAccess === 'owner'}
          onOpenChange={setEditing}
          onDeleted={() => navigate('/catalog/software/accounts')}
        />
      )}
      {canShare && (
        <OnlineAccountShareDialog
          open={sharing}
          row={row}
          shareable={shareable}
          superAdmins={superAdmins}
          profilesById={profilesById}
          onOpenChange={setSharing}
        />
      )}
      <OnlineLoginDialog
        open={loginDialog !== null}
        login={activeLogin}
        accountId={row.id}
        accountName={row.name}
        defaultRole={loginDialog?.role ?? 'user'}
        people={people}
        canEditLogin={canEdit}
        canChangePassword={canEditAccount(activeLoginAccess)}
        onOpenChange={(open) => {
          if (!open) setLoginDialog(null);
        }}
      />
    </div>
  );
}

function BackLink() {
  return (
    <Link
      to="/catalog/software/accounts"
      className="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
    >
      <ArrowLeft className="h-4 w-4" />
      Back to accounts
    </Link>
  );
}

function Muted() {
  return <span className="text-muted-foreground">—</span>;
}

function LoginsCard({
  row,
  myShares,
  profilesById,
  onAdd,
  onOpen,
}: {
  row: OnlineAccountRow;
  myShares: OnlineShare[];
  profilesById: Map<string, SeatProfile>;
  onAdd: (role: OnlineLoginRole) => void;
  onOpen: (login: OnlineLogin) => void;
}) {
  const canEdit = canEditAccount(row.myAccess);
  const admins = row.logins.filter((l) => l.role === 'admin');
  const users = row.logins.filter((l) => l.role === 'user');

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
        <div className="space-y-1">
          <CardTitle>Logins</CardTitle>
          <p className="text-sm text-muted-foreground">
            {row.myAccess === 'login'
              ? 'The logins shared with you.'
              : 'The admin login and every additional user, each with their own sign-in.'}
          </p>
        </div>
        {canEdit && (
          <Button size="sm" onClick={() => onAdd(admins.length === 0 ? 'admin' : 'user')}>
            <Plus className="h-4 w-4" />
            {admins.length === 0 ? 'Add admin login' : 'Add user'}
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-6">
        {row.logins.length === 0 ? (
          <p className="rounded-lg border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground">
            No logins yet.
            {canEdit && ' Start with the admin login.'}
          </p>
        ) : (
          <>
            {admins.length > 0 && (
              <LoginGroup
                title={admins.length === 1 ? 'Admin' : 'Admins'}
                logins={admins}
                row={row}
                myShares={myShares}
                profilesById={profilesById}
                onOpen={onOpen}
              />
            )}
            {(users.length > 0 || (canEdit && admins.length > 0)) && (
              <LoginGroup
                title={`Additional users${users.length ? ` (${users.length})` : ''}`}
                logins={users}
                row={row}
                myShares={myShares}
                profilesById={profilesById}
                onOpen={onOpen}
                emptyAction={canEdit ? () => onAdd('user') : undefined}
              />
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function LoginGroup({
  title,
  logins,
  row,
  myShares,
  profilesById,
  onOpen,
  emptyAction,
}: {
  title: string;
  logins: OnlineLogin[];
  row: OnlineAccountRow;
  myShares: OnlineShare[];
  profilesById: Map<string, SeatProfile>;
  onOpen: (login: OnlineLogin) => void;
  emptyAction?: () => void;
}) {
  return (
    <section className="space-y-2">
      <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{title}</h3>
      {logins.length === 0 && emptyAction ? (
        <button
          type="button"
          onClick={emptyAction}
          className="flex min-h-11 w-full items-center justify-center gap-2 rounded-lg border border-dashed border-border px-4 text-sm text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
        >
          <Plus className="h-4 w-4" />
          Add a user with their own login
        </button>
      ) : (
        <div className="space-y-3">
          {logins.map((login) => (
            <LoginItem
              key={login.id}
              login={login}
              access={loginAccessFor(login, row.myAccess, myShares)}
              accountEditable={canEditAccount(row.myAccess)}
              profilesById={profilesById}
              onOpen={() => onOpen(login)}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function LoginItem({
  login,
  access,
  accountEditable,
  profilesById,
  onOpen,
}: {
  login: OnlineLogin;
  access: ReturnType<typeof loginAccessFor>;
  accountEditable: boolean;
  profilesById: Map<string, SeatProfile>;
  onOpen: () => void;
}) {
  const holder = loginHolderName(login, profilesById);
  const profile = login.profile_id ? profilesById.get(login.profile_id) : undefined;
  const changedBy = profileName(login.password_updated_by, profilesById);

  return (
    <div className="rounded-lg border border-border p-3 sm:p-4">
      <div className="flex items-start gap-3">
        {holder ? (
          <PersonAvatar id={login.id} name={holder} profile={profile} />
        ) : (
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-secondary text-muted-foreground">
            <KeyRound className="h-4 w-4" />
          </span>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="truncate font-medium text-foreground">
              {holder ?? (login.role === 'admin' ? 'Admin login' : 'Shared login')}
            </span>
            <LoginRoleBadge role={login.role} />
            {login.role_detail && <Badge variant="outline">{login.role_detail}</Badge>}
          </div>
          {holder && !login.profile_id && (
            <div className="text-xs text-muted-foreground">Not in Atlas</div>
          )}
        </div>
        {accountEditable ? (
          <Button size="sm" variant="ghost" className="h-9 shrink-0" onClick={onOpen}>
            <Pencil className="h-4 w-4" />
            Edit
          </Button>
        ) : canEditAccount(access) ? (
          <Button size="sm" variant="ghost" className="h-9 shrink-0" onClick={onOpen}>
            <KeyRound className="h-4 w-4" />
            Change password
          </Button>
        ) : null}
      </div>

      <dl className="mt-3 grid gap-x-6 gap-y-2 sm:grid-cols-2">
        <LoginField label="Username">
          {login.username ? (
            <span className="inline-flex max-w-full items-center gap-1">
              <span className="truncate">{login.username}</span>
              <CopyButton value={login.username} label="Username" />
            </span>
          ) : (
            <Muted />
          )}
        </LoginField>
        <LoginField label="Email">
          {login.email ? (
            <span className="inline-flex max-w-full items-center gap-1">
              <span className="truncate">{login.email}</span>
              <CopyButton value={login.email} label="Email" />
            </span>
          ) : (
            <Muted />
          )}
        </LoginField>
        <LoginField label="Password">
          <SavedPassword
            key={login.password_updated_at ?? 'none'}
            loginId={login.id}
            hasPassword={login.has_password}
          />
        </LoginField>
        <LoginField label="Two-step">
          {login.two_factor ? <span className="break-words">{login.two_factor}</span> : <Muted />}
        </LoginField>
      </dl>

      {login.notes && (
        <p className="mt-3 whitespace-pre-wrap text-sm text-muted-foreground">{login.notes}</p>
      )}
      {login.password_updated_at && (
        <p className="mt-3 text-xs text-muted-foreground">
          Password saved {formatDate(login.password_updated_at)}
          {changedBy ? ` by ${changedBy}` : ''}
        </p>
      )}
    </div>
  );
}

function LoginField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="flex min-h-9 min-w-0 items-center text-sm text-foreground">{children}</dd>
    </div>
  );
}

function DetailsCard({ row }: { row: OnlineAccountRow }) {
  const href = websiteHref(row.website);
  const subscriptions = useSoftwareSubscriptions();
  const subscription = row.subscription_id
    ? (subscriptions.data ?? []).find((s) => s.id === row.subscription_id)
    : undefined;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle>On file</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <KV
          label="Sign in"
          value={
            href && row.website ? (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex max-w-full items-center gap-1 text-atmosphere-dark hover:underline"
              >
                <span className="truncate">{websiteLabel(row.website)}</span>
                <ExternalLink className="h-3.5 w-3.5 shrink-0" />
              </a>
            ) : (
              <Muted />
            )
          }
        />
        <KV
          label="Account #"
          value={
            row.account_number ? (
              <span className="inline-flex max-w-full items-center gap-1">
                <span className="truncate">{row.account_number}</span>
                <CopyButton value={row.account_number} label="Account number" />
              </span>
            ) : (
              <Muted />
            )
          }
        />
        <KV
          label="Phone"
          value={
            row.phone ? (
              <a href={`tel:${row.phone}`} className="text-atmosphere-dark hover:underline">
                {formatPhone(row.phone)}
              </a>
            ) : (
              <Muted />
            )
          }
        />
        <KV
          label="Address"
          value={row.address ? <span className="whitespace-pre-wrap">{row.address}</span> : <Muted />}
        />
        {subscription && (
          <KV
            label="Subscription"
            value={
              <Link
                to={`/catalog/software/subscriptions/${subscription.id}`}
                className="text-atmosphere-dark hover:underline"
              >
                {subscription.name}
              </Link>
            }
          />
        )}
      </CardContent>
    </Card>
  );
}

function CreatedCard({
  row,
  profilesById,
}: {
  row: OnlineAccountRow;
  profilesById: Map<string, SeatProfile>;
}) {
  const age = durationSince(row.opened_on);
  const addedBy = profileName(row.created_by, profilesById);
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle>Created</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <KV
          label="Created on"
          value={
            row.opened_on ? (
              <span>
                {formatDateOnly(row.opened_on)}
                {age && <span className="block text-xs text-muted-foreground">{age}</span>}
              </span>
            ) : (
              <Muted />
            )
          }
        />
        <KV label="Created by" value={row.openedByName ?? <Muted />} />
        <p className="pt-2 text-xs text-muted-foreground">
          Added to Atlas {formatDate(row.created_at)}
          {addedBy ? ` by ${addedBy}` : ''}.
        </p>
      </CardContent>
    </Card>
  );
}

function AccessCard({
  row,
  myShares,
  superAdmins,
  profilesById,
  onShare,
}: {
  row: OnlineAccountRow;
  myShares: OnlineShare[];
  superAdmins: SeatProfile[];
  profilesById: Map<string, SeatProfile>;
  onShare: () => void;
}) {
  if (!canShareAccount(row.myAccess)) {
    const mine = myShares.filter((s) => s.account_id === row.id && isShareActive(s));
    const first = mine[0];
    const grantedBy = profileName(first?.granted_by, profilesById);
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle>Your access</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          {row.myAccess && <AccessBadge access={row.myAccess} />}
          <p className="text-muted-foreground">
            {row.myAccess === 'login'
              ? `You can see ${mine.length === 1 ? 'one login' : `${mine.length} logins`} on this account.`
              : row.myAccess === 'view' || row.myAccess === 'edit'
                ? SHARE_LEVEL_DESCRIPTIONS[row.myAccess]
                : null}
          </p>
          {first && (
            <p className="text-xs text-muted-foreground">
              Shared {grantedBy ? `by ${grantedBy} ` : ''}on {formatDate(first.created_at)}
              {first.expires_at ? `, until ${formatDate(first.expires_at)}` : ''}.
            </p>
          )}
        </CardContent>
      </Card>
    );
  }

  const active = row.people
    .filter(hasLiveAccess)
    .map((person) => ({
      ...person,
      name: profileName(person.profileId, profilesById) ?? 'Former member',
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0 pb-3">
        <CardTitle>Who can see this</CardTitle>
        <Button size="sm" variant="outline" onClick={onShare}>
          <Share2 className="h-4 w-4" />
          Share
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-3">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-info-soft text-info">
            <ShieldCheck className="h-4 w-4" />
          </span>
          <span className="min-w-0 text-sm">
            <span className="block text-foreground">Super admins</span>
            <span className="block truncate text-xs text-muted-foreground">
              {superAdmins.map((p) => p.full_name || p.email).join(', ') || 'Every account'}
            </span>
          </span>
        </div>
        {active.map((person) => (
          <div key={person.profileId} className="flex items-center gap-3">
            <PersonAvatar
              id={person.profileId}
              name={person.name}
              profile={profilesById.get(person.profileId)}
            />
            <span className="min-w-0 text-sm">
              <span className="block truncate text-foreground">{person.name}</span>
              <span className="block truncate text-xs text-muted-foreground">
                {describeAccess(person, row)}
                {person.expiresAt ? ` · until ${formatDate(person.expiresAt)}` : ''}
              </span>
            </span>
          </div>
        ))}
        {active.length === 0 && (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Lock className="h-3.5 w-3.5" />
            Nobody else can see this account.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
